<template>
  <div class="device-label-component">
    <!-- 纯净模式切换 -->
    <div
      class="pure-mode-toggle"
      v-if="showModeToggle"
    >
      <button
        @click="togglePureMode"
        :class="{ active: pureMode }"
        class="pure-mode-btn"
      >
        <i class="el-icon-view"></i>
        {{ pureMode ? "退出纯净模式" : "纯净模式" }}
      </button>
    </div>

    <!-- 新增标签按钮（右上角） -->
    <div
      class="add-label-btn"
      v-if="!pureMode"
      @click="startAddLabel"
    >
      <i class="el-icon-plus"></i>
      {{ isPlacingLabel ? "取消放置" : "添加设备标签" }}
    </div>

    <!-- 设备标签列表悬浮窗 -->
    <div
      class="labels-panel"
      v-if="!pureMode && labels.length > 0 && !showLabelDialog"
      :class="{ collapsed: isLabelsPanelCollapsed }"
    >
      <div
        class="panel-header"
        @click="toggleLabelsPanel"
      >
        <div class="header-title">
          <i class="el-icon-tickets"></i>
          <span class="title-text">设备标签列表</span>
          <span class="label-count">({{ labels.length }})</span>
        </div>
        <i class="el-icon-arrow-up collapse-icon"></i>
      </div>

      <div
        class="panel-content"
        v-if="!isLabelsPanelCollapsed"
      >
        <div
          class="label-item"
          v-for="(label, index) in labels"
          :key="index"
        >
          <div
            class="label-info"
            @click="focusOnLabel(label)"
          >
            <div
              class="label-color"
              :style="{ backgroundColor: getLabelColor(label.type) }"
            ></div>
            <div class="label-text">
              <div class="label-name">{{ label.labelname }}</div>
              <div class="label-position">
                位置: ({{ label.x.toFixed(1) }}, {{ label.y.toFixed(1) }})
              </div>
            </div>
          </div>
          <i
            class="el-icon-delete delete-icon"
            @click.stop="removeLabel(label.id)"
            title="删除标签"
          ></i>
        </div>
      </div>
    </div>

    <!-- 图片容器 -->
    <div
      class="image-container"
      ref="container"
      :class="{ 'placing-label': isPlacingLabel }"
    >
      <div
        class="image-wrapper"
        :style="imageWrapperStyle"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
        @click="placeLabel"
      >
        <!-- 图片 -->
        <img
          ref="image"
          :src="imageUrl"
          alt="监控图像"
          @load="onImageLoad"
          @wheel="onWheel"
        />

        <!-- 放置标签时的预览 -->
        <div
          v-if="isPlacingLabel && labelPreviewPosition"
          class="label-preview"
          :style="getLabelPreviewStyle()"
        >
          <div class="device-marker">
            <div
              class="device-icon"
              style="background-color: #1890ff"
            >
              <i class="el-icon-plus"></i>
            </div>
            <div class="device-name">新标签</div>
          </div>
          <div class="preview-hint">点击放置标签</div>
        </div>

        <!-- 设备标签 -->
        <div
          v-for="(label, inx) in labels"
          :key="inx"
          class="device-label"
          :class="{
            selected: selectedLabelId === label.id && !rightClickedLabelId,
            'right-clicked': rightClickedLabelId === label.id,
          }"
          :style="getLabelStyle(label)"
          @click.left.stop="handleLabelClick($event, label)"
          @mouseenter="onLabelMouseEnter(label.id)"
          @mouseleave="onLabelMouseLeave"
          @contextmenu.prevent="showDeleteButton($event, label)"
        >
          <!-- 设备标识 -->
          <div class="device-marker">
            <div
              class="device-icon"
              :style="{ backgroundColor: getLabelColor(label.type) }"
            >
              <i :class="getLabelIcon(label.type)"></i>
            </div>
            <div class="device-name">{{ label.labelname }}</div>
          </div>

          <!-- 详细信息（左键点击显示） -->
          <div
            class="device-details"
            v-if="selectedLabelId === label.id && !rightClickedLabelId"
            @click.stop
          >
            <div class="details-header">
              <h4>{{ label.labelname }} - 设备详情</h4>
              <i
                class="el-icon-close"
                @click.stop="deselectLabel"
              ></i>
            </div>
            <div class="details-content">
              <div class="detail-row">
                <span class="detail-label">设备类型:</span>
                <span class="detail-value">{{
                  getDeviceTypeText(label.devicetype) || "未设置"
                }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">设备名称:</span>
                <span
                  class="detail-value"
                  :title="
                    label.zhgddevice ? label.zhgddevice.device_name : '--'
                  "
                >{{
                    label.zhgddevice ? label.zhgddevice.device_name : "--"
                  }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">设备编号:</span>
                <span
                  class="detail-value"
                  :titile="label.zhgddevice ? label.zhgddevice.device_id : '--'"
                >{{
                    label.zhgddevice ? label.zhgddevice.device_id : "--"
                  }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">标签名称:</span>
                <span class="detail-value">{{ label.labelname }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 1"
              >
                <span class="detail-label">AI播报:</span>
                <span class="detail-value">{{
                  label.aibroadcast ? label.aibroadcast.count : ""
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 1"
              >
                <span class="detail-label">抓拍照片:</span>
                <span class="detail-value">
                  <el-image
                    :src="
                      label.aibroadcast
                        ? $store.state.baseUrl + label.aibroadcast.photo
                        : ''
                    "
                    fit="fill"
                    :preview-src-list="[
                      label.aibroadcast
                        ? $store.state.baseUrl + label.aibroadcast.photo
                        : '',
                    ]"
                  ></el-image>
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 1"
              >
                <el-button
                  type="text"
                  @click="showMonitoring(label)"
                >查看监控</el-button>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">AQI:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.AQI + "" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">TSP:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.TSP + " u/m³" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">PM2.5:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.PM + " mg/m³" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">PM10:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.PM10 + " mg/m³" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">噪音:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.noise + " dB" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">温度:</span>
                <span class="detail-value">{{
                  label.environment
                    ? label.environment.temperature + " ℃"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">湿度:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.humidity + " %" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">风速:</span>
                <span class="detail-value">{{
                  label.environment
                    ? label.environment.windspeed + " m/s"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">风级:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.windscale : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">风向:</span>
                <span class="detail-value">{{
                  label.environment ? label.environment.winddirection : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 2"
              >
                <span class="detail-label">预警等级:</span>
                <span
                  class="detail-value"
                  :title="
                    label.environment&&label.environment.worninginfos&&label.environment.worninginfos.length
                      ? label.environment.worninginfos[0].worningcount
                      : '--'
                  "
                >{{
                    label.environment
                      ? label.environment.worninginfos&&label.environment.worninginfos.length
                        ? label.environment.worninginfos[0].worningcount
                        : "--"
                      : "--"
                  }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">高度:</span>
                <span class="detail-value">{{
                  label.mechanicalstd ? label.mechanicalstd.Height + " m" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">幅度:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.amplitude + " m"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">额重:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.RatedLoad + " t"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">吊重:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.ActualLoad + " t"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">回旋:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.Rotation + " °"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">倾角:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.Inclination + " °"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">力矩:</span>
                <span class="detail-value">{{
                  label.mechanicalstd ? label.mechanicalstd.Torque + " °" : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">风速:</span>
                <span class="detail-value">{{
                  label.mechanicalstd
                    ? label.mechanicalstd.WindSpeed + " m/s"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">设备状态:</span>
                <span class="detail-value">{{
                  label.mechanicalstd.Status == 1 ? "在线" : "离线"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">预警等级:</span>
                <span class="detail-value">{{
                  label.mechanicalstd.WarningLevel
                    ? label.mechanicalstd.WarningLevel
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 3"
              >
                <span class="detail-label">预警内容:</span>
                <span class="detail-value">{{
                  label.mechanicalstd.WarningContent
                    ? label.mechanicalstd.WarningContent
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">高度:</span>
                <span class="detail-value">{{ label.mechanicalsjj.Height }} m</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">楼层:</span>
                <span class="detail-value">{{ label.mechanicalsjj.Floor }} 层</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">运动方向:</span>
                <span class="detail-value">{{ label.mechanicalsjj.RunningDirection }}
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">运行速度:</span>
                <span class="detail-value">{{ label.mechanicalsjj.RunningSpeed }}
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">前门状态:</span>
                <span class="detail-value">{{ label.mechanicalsjj.FrontDoorStatus }}
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">后门状态:</span>
                <span class="detail-value">{{ label.mechanicalsjj.RearDoorStatus }}
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">载重:</span>
                <span class="detail-value">{{ label.mechanicalsjj.Load }} t</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">驾驶员:</span>
                <span class="detail-value">{{
                  label.mechanicalsjj.Driver
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">身份证号:</span>
                <span class="detail-value">{{
                  label.mechanicalsjj.IDNum
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">工号:</span>
                <span class="detail-value">{{
                  label.mechanicalsjj.EmployeeNumber
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 4"
              >
                <span class="detail-label">设备状态:</span>
                <span
                  class="detail-value"
                  :style="{
                    color: label.mechanicalsjj.Status == 0 ? '#00BCD4' : 'red',
                  }"
                >{{ label.mechanicalsjj.Status == 0 ? "在线" : "离线" }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 5"
              >
                <span class="detail-label">用水量:</span>
                <span class="detail-value">{{ label.energymonitoring_s.Consumption }} m³</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 5"
              >
                <span class="detail-label">预警内容:</span>
                <span
                  class="detail-value"
                  :title="label.energymonitoring_s?label.energymonitoring_s.worninginfos[0].worningcount:'--'"
                >{{
                    label.energymonitoring_s?label.energymonitoring_s.worninginfos[0].worningcount:'--'
                  }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 6"
              >
                <span class="detail-label">用电量:</span>
                <span class="detail-value">{{ label.energymonitoring_d?label.energymonitoring_d.Consumption:'--' }} kw-h</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 6"
              >
                <span class="detail-label">预警内容:</span>
                <span
                  class="detail-value"
                  :title="label.energymonitoring_d?label.energymonitoring_d.worninginfos[0].worningcount:'--'"
                >{{
                    label.energymonitoring_d?label.energymonitoring_d.worninginfos[0].worningcount:'--'
                  }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 7"
              >
                <span class="detail-label">车牌号:</span>
                <span class="detail-value">{{
                  label.vehiclemanage ? label.vehiclemanage.licenseplate : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 7"
              >
                <span class="detail-label">进出类型:</span>
                <span class="detail-value">{{
                  label.vehiclemanage
                    ? label.vehiclemanage.inouttype == 0
                      ? "车辆进场"
                      : "车辆出场"
                    : "--"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 7"
              >
                <span class="detail-label">是否清洗:</span>
                <span class="detail-value">{{
                  label.vehiclemanage && label.vehiclemanage.inouttype == 0
                    ? "车辆进场"
                    : "车辆出场"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 7"
              >
                <span class="detail-label">抓拍照片:</span>
                <span class="detail-value">
                  <el-image
                    :src="
                      label.vehiclemanage
                        ? $store.state.baseUrl + label.vehiclemanage.photo
                        : ''
                    "
                    fit="fill"
                    :preview-src-list="[
                      label.vehiclemanage
                        ? $store.state.baseUrl + label.vehiclemanage.photo
                        : '',
                    ]"
                  ></el-image>
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">姓名:</span>
                <span class="detail-value">{{ label.personnelinfo.Name }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">性别:</span>
                <span class="detail-value">{{
                  label.personnelinfo.Gender == 0 ? "男" : "女"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">年龄:</span>
                <span class="detail-value">{{ label.personnelinfo.Age }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">证件照:</span>
                <span class="detail-value">
                  <el-image
                    style="width: 60px;"
                    :src="
                      label.personnelinfo
                        ? $store.state.baseUrl + label.personnelinfo.PhotoPath
                        : ''
                    "
                    fit="fill"
                    :preview-src-list="[
                      label.personnelinfo
                        ? $store.state.baseUrl + label.personnelinfo.PhotoPath
                        : '',
                    ]"
                  ></el-image>
                </span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">民族:</span>
                <span class="detail-value">{{
                  label.personnelinfo.Ethnicity
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">籍贯:</span>
                <span class="detail-value">{{
                  label.personnelinfo.address
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">身份证地址:</span>
                <span class="detail-value">{{
                  label.personnelinfo.IDCardAddress
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">身份证有效期限:</span>
                <span class="detail-value">{{
                  label.personnelinfo.IDCardDate
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">手机号:</span>
                <span class="detail-value">{{
                  label.personnelinfo.PhoneNumber
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 8"
              >
                <span class="detail-label">在册状态:</span>
                <span class="detail-value">{{
                  label.personnelinfo.RegistrationStatus == 0
                    ? "在册"
                    : "已退场"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 9"
              >
                <span class="detail-label">姓名:</span>
                <span class="detail-value">{{
                  label.attendancerecord.Name
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 9"
              >
                <span class="detail-label">所属班组:</span>
                <span class="detail-value">{{
                  label.attendancerecord.Class
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 9"
              >
                <span class="detail-label">所属工种:</span>
                <span class="detail-value">{{
                  label.attendancerecord.WorkType
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 9"
              >
                <span class="detail-label">进出类型:</span>
                <span class="detail-value">{{
                  label.attendancerecord.InOutType == 0 ? "进场" : "出场"
                }}</span>
              </div>
              <div
                class="detail-row"
                v-if="label.devicetype == 9"
              >
                <span class="detail-label">证件照:</span>
                <span class="detail-value">
                  <el-image
                    style="width: 60px;"
                    :src="
                      label.attendancerecord
                        ? $store.state.baseUrl +
                          label.attendancerecord.AttendancePhoto
                        : ''
                    "
                    fit="fill"
                    :preview-src-list="[
                      label.attendancerecord
                        ? $store.state.baseUrl +
                          label.attendancerecord.AttendancePhoto
                        : '',
                    ]"
                  ></el-image>
                </span>
              </div>
            </div>
          </div>

          <!-- 右键删除按钮（移到device-details外部） -->
          <div
            v-if="rightClickedLabelId === label.id && !pureMode"
            class="delete-button"
            @click.stop="removeLabel(label.id)"
          >
            <i class="el-icon-delete"></i>
            <span>删除</span>
          </div>
        </div>

        <!-- 控制按钮（纯净模式可隐藏） -->
        <!-- v-if="!pureMode && !isPlacingLabel" -->
        <div class="view-controls">
          <div class="zoom-controls">
            <button
              @click="zoomIn"
              title="放大"
            >
              <i class="el-icon-zoom-in"></i>
            </button>
            <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
            <button
              @click="zoomOut"
              title="缩小"
            >
              <i class="el-icon-zoom-out"></i>
            </button>
            <button
              @click="resetZoom"
              title="重置"
            >
              <i class="el-icon-refresh"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 新增标签对话框 -->
      <div
        class="dialog-overlay"
        v-if="showLabelDialog"
        @click.self="closeLabelDialog"
      >
        <div class="label-dialog">
          <div class="dialog-header">
            <h3>新增设备标签</h3>
            <i
              class="el-icon-close close-btn"
              @click="closeLabelDialog"
            ></i>
          </div>
          <div class="dialog-body">
            <div class="position-info">
              <i class="el-icon-location-information"></i>
              已选择位置: ({{ newLabelPosition.x.toFixed(1) }},
              {{ newLabelPosition.y.toFixed(1) }})
            </div>
            <div class="form-group">
              <label>标签名称 <span class="required">*</span></label>
              <el-input
                v-model="bindingInfo.formData.labelname"
                placeholder="请输入名称"
              ></el-input>
            </div>
            <div class="form-group">
              <label>设备类型 <span class="required">*</span></label>
              <el-select
                v-model="bindingInfo.formData.devicetype"
                @change="GetZhgdDevicesFC"
                placeholder="请选择"
                clearable
                filterable
              >
                <el-option
                  v-for="item in bindingInfo.selectTyleList"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </div>
            <div class="form-group">
              <label>选择设备 <span class="required">*</span></label>
              <el-select
                v-model="bindingInfo.formData.deviceid"
                value-key="device_id"
                clearable
                filterable
              >
                <el-option
                  v-for="item in bindingInfo.selectDeviceList"
                  :key="item.id"
                  :label="item.device_name"
                  :value="item.id"
                >
                </el-option>
              </el-select>
            </div>
          </div>
          <div class="dialog-footer">
            <el-button @click="closeLabelDialog">取 消</el-button>
            <el-button
              type="primary"
              @click="saveLabelFromDialog"
            >确 定</el-button>
          </div>
        </div>
      </div>
    </div>
    <el-dialog
      title="监控"
      append-to-body
      :visible.sync="viedo_show"
      destroy-on-close
      width="40%"
    >
      <EZUIKit
        :config="ActiveVideoItem"
        v-if="viedo_show"
        style="height:600px"
      ></EZUIKit>
    </el-dialog>
  </div>
</template>

<script>
import { GetZhgdDevices, ViewCarema } from "@/setAxios";
import EZUIKit from "@/components/EZUIKit/index.vue";
export default {
  components: { EZUIKit },
  name: "DeviceLabelComponent",
  props: {
    imageUrl: {
      type: String,
      default: "",
    },
    showModeToggle: {
      type: Boolean,
      default: false,
    },
    pureViewMode: {
      type: Boolean,
      default: false,
    },
    initialLabels: {
      type: Array,
      default: () => [],
    },
    // 新增：传入背景ID
    backgroundId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      ActiveVideoItem: null,
      viedo_show: false,
      pureMode: this.pureViewMode,
      scale: 1,
      minScale: 0.2,
      maxScale: 3,
      position: { x: 0, y: 0 },
      isDraggingImage: false,
      dragStart: { x: 0, y: 0 },
      imageSize: { width: 0, height: 0 },
      containerSize: { width: 0, height: 0 },
      labels: [],
      selectedLabelId: null,
      hoveredLabelId: null,
      rightClickedLabelId: null,
      showLabelDialog: false,

      isPlacingLabel: false,
      labelPreviewPosition: null,
      newLabelPosition: { x: 0, y: 0 },

      isLabelsPanelCollapsed: false,

      nextId: 1,
      deviceTypeConfig: {
        camera: { icon: "el-icon-video-camera", color: "#ff4d4f" },
        sensor: { icon: "el-icon-c-scale-to-original", color: "#52c41a" },
        alarm: { icon: "el-icon-bell", color: "#faad14" },
        other: { icon: "el-icon-monitor", color: "#1890ff" },
      },
      bindingInfo: {
        formData: {
          xid: localStorage.xid,
          backgroundid: "", // 将使用传入的backgroundId
          deviceid: "",
          devicetype: "1",
          labelname: "",
          position: "",
        },
        selectTyleList: [
          { value: "1", label: "数据看板-智能监控" },
          { value: "2", label: "环境监测" },
          { value: "3", label: "机械监测-塔吊" },
          { value: "4", label: "机械监测-升降机" },
          { value: "5", label: "能耗监测-用水" },
          { value: "6", label: "能耗监测-用电" },
          { value: "7", label: "车辆管理" },
          { value: "8", label: "劳务管理-分包管理" },
          { value: "9", label: "劳务管理-出勤管理" },
        ],
        selectDeviceList: [],
      },
    };
  },
  computed: {
    imageWrapperStyle() {
      return {
        transform: `translate(${this.position.x}px, ${this.position.y}px) scale(${this.scale})`,
        transformOrigin: "0 0",
        cursor: this.isDraggingImage ? "grabbing" : "grab",
      };
    },
  },
  watch: {
    backgroundId: {
      immediate: true,
      handler(newVal) {
        this.bindingInfo.formData.backgroundid = newVal;
      },
    },
  },
  mounted() {
    this.initLabels();
    this.updateContainerSize();
    window.addEventListener("resize", this.updateContainerSize);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("click", this.hideDeleteButton);
    window.addEventListener("contextmenu", this.hideDeleteButton);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateContainerSize);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("click", this.hideDeleteButton);
    window.removeEventListener("contextmenu", this.hideDeleteButton);
  },
  watch: {
    initialLabels: {
      handler(newVal) {
        this.labels = JSON.parse(JSON.stringify(newVal));
        console.log(this.labels);

        this.updateNextId();
      },
      deep: true,
    },
  },
  methods: {
    showMonitoring(row) {
      ViewCarema({ id: row.deviceid, xid: this.$store.state.xid }).then(
        (res) => {
          if (res.Data && res.Data.length) {
            this.ActiveVideoItem = {
              id: row.id,
              url: res.Data[0].url,
              token: res.Data[0].token,
            };
            this.viedo_show = true;
          } else {
            this.$message.error("监控无效");
          }
        }
      );
    },
    // 初始化
    initLabels() {
      this.labels = JSON.parse(JSON.stringify(this.initialLabels));
      this.updateNextId();
    },

    updateNextId() {
      if (this.labels.length > 0) {
        this.nextId = Math.max(...this.labels.map((l) => l.id)) + 1;
      } else {
        this.nextId = 1;
      }
    },

    updateContainerSize() {
      if (this.$refs.container) {
        this.containerSize = {
          width: this.$refs.container.clientWidth,
          height: this.$refs.container.clientHeight,
        };
      }
    },

    onImageLoad() {
      const img = this.$refs.image;
      if (img) {
        this.imageSize = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        this.centerImage();
      }
    },

    centerImage() {
      if (!this.imageSize.width || !this.imageSize.height) return;

      const scaleX = this.containerSize.width / this.imageSize.width;
      const scaleY = this.containerSize.height / this.imageSize.height;
      this.scale = Math.min(scaleX, scaleY, 1);

      const scaledWidth = this.imageSize.width * this.scale;
      const scaledHeight = this.imageSize.height * this.scale;

      this.position = {
        x: (this.containerSize.width - scaledWidth) / 2,
        y: (this.containerSize.height - scaledHeight) / 2,
      };
    },

    // 模式切换
    togglePureMode() {
      this.pureMode = !this.pureMode;
      if (this.pureMode) {
        this.resetZoom();
        this.deselectLabel();
        this.cancelPlaceLabel();
        this.hideDeleteButton();
      }
      this.$emit("mode-change", this.pureMode ? "pure" : "normal");
    },

    // 标签面板
    toggleLabelsPanel() {
      this.isLabelsPanelCollapsed = !this.isLabelsPanelCollapsed;
    },

    // 聚焦到标签
    focusOnLabel(label) {
      const centerX = (label.x / 100) * this.imageSize.width * this.scale;
      const centerY = (label.y / 100) * this.imageSize.height * this.scale;

      this.position = {
        x: this.containerSize.width / 2 - centerX,
        y: this.containerSize.height / 2 - centerY,
      };

      this.selectLabel(label.id);
    },

    // 缩放控制
    onWheel(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      this.zoom(delta, { x: event.offsetX, y: event.offsetY });
    },

    zoom(delta, center) {
      const oldScale = this.scale;
      this.scale = Math.max(
        this.minScale,
        Math.min(this.maxScale, this.scale + delta)
      );

      if (center && oldScale !== 0) {
        const scaleRatio = this.scale / oldScale;
        this.position.x = center.x - (center.x - this.position.x) * scaleRatio;
        this.position.y = center.y - (center.y - this.position.y) * scaleRatio;
      }
    },

    zoomIn() {
      this.zoom(0.1, {
        x: this.containerSize.width / 2,
        y: this.containerSize.height / 2,
      });
    },

    zoomOut() {
      this.zoom(-0.1, {
        x: this.containerSize.width / 2,
        y: this.containerSize.height / 2,
      });
    },

    resetZoom() {
      this.scale = 1;
      this.centerImage();
    },

    // 图片拖拽
    onMouseDown(event) {
      if (this.isPlacingLabel) return;
      this.isDraggingImage = true;
      this.dragStart = {
        x: event.clientX - this.position.x,
        y: event.clientY - this.position.y,
      };
      event.preventDefault();
    },

    onMouseMove(event) {
      if (this.isDraggingImage) {
        this.position = {
          x: event.clientX - this.dragStart.x,
          y: event.clientY - this.dragStart.y,
        };
      } else if (this.isPlacingLabel) {
        this.updateLabelPreview(event);
      }
    },

    onMouseUp() {
      this.isDraggingImage = false;
    },

    updateLabelPreview(event) {
      const rect = this.$refs.container.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      const x =
        ((offsetX - this.position.x) / this.scale / this.imageSize.width) * 100;
      const y =
        ((offsetY - this.position.y) / this.scale / this.imageSize.height) *
        100;

      this.labelPreviewPosition = {
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
      };
    },

    // 标签操作
    getLabelStyle(label) {
      return {
        left: `${label.x}%`,
        top: `${label.y}%`,
        transform: "translate(-50%, -50%)",
      };
    },

    getLabelPreviewStyle() {
      if (!this.labelPreviewPosition) return {};
      return {
        left: `${this.labelPreviewPosition.x}%`,
        top: `${this.labelPreviewPosition.y}%`,
        transform: "translate(-50%, -50%)",
      };
    },

    onLabelMouseEnter(labelId) {
      this.hoveredLabelId = labelId;
    },

    onLabelMouseLeave() {
      this.hoveredLabelId = null;
    },

    // 标签点击处理
    handleLabelClick(event, label) {
      if (this.isPlacingLabel) return;

      console.log(label);

      // 左键点击显示设备详情
      this.selectedLabelId =
        this.selectedLabelId === label.id ? null : label.id;
      this.hideDeleteButton(); // 点击标签时隐藏删除按钮
    },

    selectLabel(labelId) {
      if (this.isPlacingLabel) return;
      this.selectedLabelId = labelId;
    },

    deselectLabel() {
      this.selectedLabelId = null;
    },

    // 新增标签
    onKeyDown(event) {
      if (event.key === "Escape" && this.isPlacingLabel) {
        this.cancelPlaceLabel();
      }
    },

    startAddLabel() {
      if (this.isPlacingLabel) {
        this.cancelPlaceLabel();
        return;
      }
      this.isPlacingLabel = true;
      this.labelPreviewPosition = { x: 50, y: 50 };
      this.deselectLabel();
      this.hideDeleteButton();
    },

    cancelPlaceLabel() {
      this.isPlacingLabel = false;
      this.labelPreviewPosition = null;
    },

    placeLabel(event) {
      if (!this.isPlacingLabel) return;
      event.stopPropagation();

      if (this.labelPreviewPosition) {
        this.newLabelPosition = { ...this.labelPreviewPosition };
        this.cancelPlaceLabel();
        this.showLabelDialog = true;
        this.resetForm();
      }
    },

    // 右键显示删除按钮
    showDeleteButton(event, label) {
      if (this.pureMode) return;

      event.stopPropagation();
      event.preventDefault();

      // 显示删除按钮，隐藏设备详情
      this.rightClickedLabelId = label.id;
      this.selectedLabelId = null;
    },

    // 隐藏删除按钮
    hideDeleteButton(event) {
      // 如果点击的是删除按钮本身，不隐藏
      if (event && event.target.closest(".delete-button")) {
        return;
      }
      this.rightClickedLabelId = null;
    },

    // 删除标签
    removeLabel(labelId) {
      const index = this.labels.findIndex((l) => l.id === labelId);
      if (index !== -1) {
        const removedLabel = this.labels[index];
        this.labels.splice(index, 1);

        if (this.selectedLabelId === labelId) {
          this.deselectLabel();
        }

        this.hideDeleteButton();
        this.$emit("label-removed", removedLabel);
      }
    },

    resetForm() {
      this.bindingInfo.formData = {
        xid: localStorage.xid,
        backgroundid: this.backgroundId,
        deviceid: "",
        devicetype: "",
        labelname: "",
        position: JSON.stringify(this.newLabelPosition),
      };
      this.bindingInfo.selectDeviceList = [];
    },

    // 设备选择
    GetZhgdDevicesFC() {
      this.bindingInfo.formData.deviceid = "";
      GetZhgdDevices({
        qid: localStorage.pid || this.$store.state.pid,
        xid: this.$store.state.xid,
        type: this.bindingInfo.formData.devicetype,
        pageIndex: 1,
        devicesourceid: 0,
        pageSize: 9999,
        searchword: "",
        status: "",
      }).then((res) => {
        this.bindingInfo.selectDeviceList = res.Data || [];
      });
    },

    // 保存标签
    validateForm() {
      const { devicetype, deviceid, labelname } = this.bindingInfo.formData;
      if (!devicetype || !deviceid || !labelname) {
        this.$message.error("请填写完整信息");
        return false;
      }
      return true;
    },

    saveLabelFromDialog() {
      if (!this.validateForm()) return;

      // 获取设备名称
      const selectedDevice = this.bindingInfo.selectDeviceList.find(
        (device) => device.id === this.bindingInfo.formData.deviceid
      );

      // 获取设备类型文本
      const deviceTypeText = this.getDeviceTypeText(
        this.bindingInfo.formData.devicetype
      );

      // 根据设备类型确定标签类型
      const labelType = this.determineLabelType(
        this.bindingInfo.formData.devicetype
      );

      const newLabel = {
        id: this.nextId++,
        labelname: this.bindingInfo.formData.labelname,
        x: this.newLabelPosition.x,
        y: this.newLabelPosition.y,
        deviceid: this.bindingInfo.formData.deviceid,
        devicetype: this.bindingInfo.formData.devicetype,
        deviceName: selectedDevice.device_name || "未知设备",
        type: labelType, // 使用真实的类型而不是固定的"other"
        // 保存原始表单数据，用于提交
        rawData: { ...this.bindingInfo.formData },
      };

      this.labels.push(newLabel);

      // 发送完整数据到外部
      this.$emit("label-added", {
        ...this.bindingInfo.formData,
        device_name: selectedDevice.device_name,
        device_type_text: deviceTypeText,
        labelType: labelType,
        x: this.newLabelPosition.x,
        y: this.newLabelPosition.y,
      });

      this.closeLabelDialog();
      //   this.$message.success("标签添加成功");
    },

    // 根据设备类型确定标签类型
    determineLabelType(deviceType) {
      const typeMapping = {
        "1": "camera", // 智能监控
        "2": "sensor", // 环境监测
        "3": "alarm", // 塔吊监测
        "4": "alarm", // 升降机监测
        "5": "sensor", // 用水监测
        "6": "sensor", // 用电监测
        "7": "other", // 车辆管理
        "8": "other", // 分包管理
        "9": "other", // 出勤管理
      };
      return typeMapping[deviceType] || "other";
    },

    // 获取设备类型文本
    getDeviceTypeText(deviceType) {
      switch (deviceType) {
        case 1:
          return "智能监控";
          break;
        case 2:
          return "环境监测";
          break;
        case 3:
          return "塔吊";
          break;
        case 4:
          return "升降机";
          break;
        case 5:
          return "用水";
          break;
        case 6:
          return "用电";
          break;
        case 7:
          return "车辆管理";
          break;

        case 8:
          return "分包管理";
          break;

        default:
          return "出勤管理";
          break;
      }
    },

    // 获取设备名称
    getDeviceName(deviceId) {
      if (!deviceId) return "未设置";
      const device = this.bindingInfo.selectDeviceList.find(
        (item) => item.id === deviceId
      );
      console.log("device", deviceId);
      return device ? device.device_name : `设备ID: ${deviceId}`;
    },

    closeLabelDialog() {
      this.showLabelDialog = false;
      this.resetForm();
    },

    // 工具方法
    getLabelColor(type) {
      return this.deviceTypeConfig.length
        ? this.deviceTypeConfig[type].color
        : "#1890ff";
    },

    getLabelIcon(type) {
      return this.deviceTypeConfig.length
        ? this.deviceTypeConfig[type].icon
        : "el-icon-monitor";
    },
  },
  created() {
    this.GetZhgdDevicesFC();
  },
};
</script>

<style scoped>
.device-label-component {
  width: 100%;
  height: 100%;
  position: relative;
  font-family: "Arial", "Microsoft YaHei", sans-serif;
}

.pure-mode-toggle {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 25;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.pure-mode-btn {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.pure-mode-btn:hover {
  background: #f5f7fa;
}

.pure-mode-btn.active {
  background: #409eff;
  color: white;
  border-color: #409eff;
}

.add-label-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.5);
  z-index: 25;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s;
}

.add-label-btn:hover {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.6);
}

/* 设备标签列表悬浮面板 */
.labels-panel {
  position: absolute;
  top: 70px;
  left: 15px;
  z-index: 25;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  width: 240px;
  max-height: 400px;
  overflow: hidden;
  transition: all 0.3s;
}

.labels-panel.collapsed {
  max-height: 40px;
}

.panel-header {
  padding: 12px 15px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
}

.panel-header:hover {
  background: #f1f3f5;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-text {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.label-count {
  font-size: 12px;
  color: #666;
  font-weight: normal;
}

.collapse-icon {
  font-size: 14px;
  color: #999;
  transition: transform 0.3s;
}

.labels-panel.collapsed .collapse-icon {
  transform: rotate(180deg);
}

.panel-content {
  max-height: 348px;
  overflow-y: auto;
  padding: 10px;
}

.label-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.label-item:last-child {
  border-bottom: none;
}

.label-item:hover {
  background-color: #f8f9fa;
  border-radius: 4px;
}

.label-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  cursor: pointer;
}

.label-info:hover {
  opacity: 0.8;
}

.label-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.label-text {
  flex: 1;
  min-width: 0;
}

.label-name {
  font-weight: 600;
  color: #333;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-position {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
}

.delete-icon {
  color: #ff4d4f;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.6;
  transition: opacity 0.2s;
  padding: 4px;
  border-radius: 4px;
}

.delete-icon:hover {
  opacity: 1;
  background-color: #ff4d4f15;
}

.image-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background-color: #f5f5f5;
}

.image-container.placing-label {
  cursor: crosshair;
}

.image-wrapper {
  position: absolute;
}

img {
  max-width: 100%;
  max-height: 100%;
  display: block;
}

.label-preview {
  position: absolute;
  z-index: 100;
  cursor: pointer;
}

.label-preview .device-marker {
  opacity: 0.8;
  border-color: #409eff !important;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(64, 158, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0);
  }
}

.preview-hint {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: #409eff;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 101;
}

.device-label {
  position: absolute;
  cursor: pointer;
  z-index: 99;
  transition: transform 0.2s;
}

.device-label:hover {
  transform: translate(-50%, -50%) scale(1.05);
  z-index: 10;
}

.device-label.selected .device-marker {
  border-color: #409eff;
  box-shadow: 0 0 10px rgba(64, 158, 255, 0.5);
}

.device-label.right-clicked .device-marker {
  border-color: #ff4d4f;
  box-shadow: 0 0 10px rgba(255, 77, 79, 0.5);
}

/* 删除按钮样式（修复后） */
.delete-button {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4d4f;
  color: white;
  border: none;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
  transition: all 0.2s;
  z-index: 30;
  white-space: nowrap;
}

.delete-button:hover {
  background: #ff7875;
  transform: translateX(-50%) scale(1.05);
  box-shadow: 0 3px 12px rgba(255, 77, 79, 0.4);
}

.delete-button:active {
  background: #d9363e;
  transform: translateX(-50%) scale(0.95);
}

.delete-button i {
  font-size: 12px;
}

.device-marker {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid;
  border-radius: 20px;
  padding: 4px 10px 4px 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;
}

.device-icon {
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  font-size: 14px;
}

.device-name {
  font-weight: bold;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.device-details {
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 240px;
  z-index: 999;
  overflow: hidden;
}

.details-header {
  background: #409eff;
  color: white;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.details-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.details-header i {
  cursor: pointer;
  font-size: 16px;
  transition: color 0.3s;
}

.details-header i:hover {
  color: #ff4d4f;
}

.details-content {
  padding: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #666;
  font-weight: 500;
}

.detail-value {
  color: #333;
  font-weight: bold;
  width: 70%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.view-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 8px 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  z-index: 1;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-controls button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #dcdfe6;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #606266;
  transition: all 0.3s;
}

.zoom-controls button:hover {
  background: #f5f7fa;
  border-color: #c6e2ff;
  color: #409eff;
}

.zoom-level {
  font-weight: bold;
  color: #333;
  min-width: 45px;
  text-align: center;
  font-size: 13px;
}

/* 自定义对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.label-dialog {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  animation: dialogFadeIn 0.3s ease;
}

@keyframes dialogFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 15px;
  border-bottom: 1px solid #eee;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  font-size: 20px;
  color: #999;
  cursor: pointer;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #ff4d4f;
}

.dialog-body {
  padding: 20px;
}

.position-info {
  background: #f0f9ff;
  border: 1px solid #c6e2ff;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 20px;
  color: #409eff;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group .required {
  color: #ff4d4f;
}

.form-group:last-child {
  margin-bottom: 0;
}

.el-select {
  width: 100%;
}

.dialog-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .pure-mode-toggle {
    top: 10px;
    left: 10px;
  }

  .add-label-btn {
    top: 10px;
    right: 10px;
    padding: 8px 16px;
    font-size: 13px;
  }

  .labels-panel {
    top: 60px;
    left: 10px;
    width: 200px;
    max-height: 350px;
  }

  .device-details {
    width: 200px;
  }

  .label-dialog {
    width: 90%;
  }
}

/* 滚动条样式 */
.panel-content::-webkit-scrollbar {
  width: 4px;
}

.panel-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.panel-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.panel-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>